/**
 * @module engine3d/renderer3d
 * @summary The software 3D rasteriser: shades, sorts and paints the scene onto
 *   an ordinary 2D canvas.
 * @description
 *   There is no WebGL here and no external library — the corporate npm registry
 *   is unreachable, and the rest of this codebase is a hand-written canvas
 *   engine, so this matches it. What that costs is a depth buffer; what
 *   replaces it is a per-triangle painter's sort, which is exact for the
 *   separated, mostly-convex solids a plant layout is made of.
 *
 *   The passes, in order:
 *     1. Sky gradient.
 *     2. Ground plane + fading grid.
 *     3. Soft shadows, accumulated on an offscreen canvas and composited once
 *        (drawing them directly would darken wherever two shadows overlapped).
 *     4. Geometry: cull → clip → project → shade → sort → fill.
 *     5. Floating labels and tags, with overlap rejection.
 *
 *   Four things carry most of the visual quality:
 *     • Every triangle is STROKED in its own fill colour as well as filled.
 *       Canvas antialiases each polygon independently, so without this every
 *       shared edge shows a hairline of background through it.
 *     • Consecutive triangles of the same colour are batched into ONE path.
 *       Sorted order groups them naturally, which cuts the fill count several
 *       fold on large scenes.
 *     • Planar quads authored as a PAIR of triangles are drawn as ONE 4-point
 *       polygon with one depth key and one shade. Half the entries to project,
 *       sort and fill for every flat face and every cylinder band — and no
 *       seam stroke down a diagonal that is not an edge of anything.
 *     • On the still frame, the edges the model AUTHORED as features (creases,
 *       rims, part joints) and the smooth edges that are on the silhouette
 *       from this viewpoint are stroked as thin ink lines: the CAD "shaded
 *       with edges" look. Nothing about an edge is inferred here — the data is
 *       the packed per-triangle `scene.feat` word (kit spec §4.1); a scene
 *       without it simply draws no lines and pairs nothing.
 *
 *   // INVARIANT: the moving frame (`seams: false`) never strokes a feature
 *   line, and neither the fill count nor the painter order depends on the
 *   `seams` setting — `perf.ts` pins both.
 *   // INVARIANT: `drawn` counts TRIANGLES (a pair is two), so the HUD figure
 *   means the same thing whether or not a scene carries pair data.
 *   // HAZARD: a pair is trusted only after its shared corners are checked
 *   bit-for-bit. A PAIR flag on a partner that is not really the other half
 *   would draw a quad spanning two unrelated triangles.
 */
import { cssColor, KEY_DIR, MATERIALS, shade, type Material } from './materials';
import { convexHull, v3, vNorm, xfPoint, type Vec2, type Vec3 } from './math3';
import { Camera } from './camera';
import { MAT_KEYS, type Scene, type SceneObject } from './scene';
import { F_DECAL, F_E0, F_E1, F_E2, F_PAIR, featDih, featEdges } from './mesh';
import type { Overlay } from './gizmo';
import { formatLength, LEG_PREFIX, legId, legRun } from './dims3';
import { FLOW_STYLES, slugAt } from './flow';

export interface RenderOptions {
  grid: boolean;
  shadows: boolean;
  labels: boolean;
  tags: boolean;
  /** Floor names and heights, marked down the near edge of the plant. */
  floors: boolean;
  /** Dimension every straight pipe leg. */
  dims: boolean;
  /** Only dimension the selected run. Ignored when nothing is selected. */
  dimsSelectedOnly: boolean;
  /** Millimetres per world unit, for the dimension text. */
  scaleMm: number;
  /**
   * Ghost everything that is not selected, instead of hiding it. On a plant
   * with a hundred items that is the difference between finding the run you
   * are working on and hunting for it — and unlike hiding, the context stays.
   */
  isolate: boolean;
  /**
   * Seal the hairline between adjacent triangles by stroking each run in its
   * own fill colour. It is a SECOND full rasterisation of every path, so it
   * roughly doubles the cost of the geometry pass — worth it on the still
   * frame anybody actually reads, and dropped while the camera is moving,
   * where a sub-pixel seam is invisible. Omitted means on — a caller that has
   * not thought about it wants the good frame.
   */
  seams?: boolean;
  /**
   * The plant running: material and air travelling along the pipework. Null,
   * or absent, leaves every pipe as bare metal.
   *
   * `travel` is elapsed animation SECONDS, not a distance. Each stream turns it
   * into its own distance at its own speed, which is what keeps material and
   * air in step with one another however long the animation has been on — and
   * what lets the speed slider change both without either jumping.
   */
  flow?: { travel: number } | null;
  /** Vertical exaggeration — 1 is true scale. */
  heightScale: number;
  selected: Set<string>;
  hoveredId: string | null;
  /**
   * The run being drawn, start to cursor, so its legs can be measured as they
   * are dragged. Absent when nothing is being drawn.
   */
  draftPts?: { x: number; y: number; z: number }[];
}

const SELECT_TINT: [number, number, number] = [0.06, 0.53, 0.92];
const HOVER_TINT: [number, number, number] = [0.42, 0.76, 1];
/**
 * Scratch for the flow tint. Reused rather than allocated: this is written and
 * consumed inside one iteration of the triangle loop, and a fresh triple per
 * pipe triangle per frame is a few hundred thousand objects a second handed
 * straight to the collector.
 */
const FLOW_TINT: [number, number, number] = [0, 0, 0];

/**
 * Depth buckets for the painter's sort.
 * The residual ordering error is one bucket width — the scene's depth span
 * divided by this. At 16k buckets over a plant a few thousand units deep that
 * is well under a millimetre of plate thickness, so nothing visibly swaps.
 */
const BUCKETS = 16384;

/**
 * Equipment label type. Small, but no longer so small its counters close up:
 * the text sits on a plate now, so it does not need a halo thick enough to
 * survive dark machinery.
 */
const LABEL_FONT = '11.5px Segoe UI, system-ui';
const LABEL_FONT_NEAR = '600 11.5px Segoe UI, system-ui';
const LABEL_FONT_SEL = 'bold 12px Segoe UI, system-ui';
const TAG_FONT = 'bold 10px Segoe UI, system-ui';
/** Chip height — and the row height one label reserves for overlap rejection. */
const LABEL_H = 18;
const TAG_H = 15;
/** Horizontal padding inside a label chip. */
const LABEL_PAD = 7;
/**
 * An item smaller than this on screen has not earned a name — its plate would
 * be wider than the machine it points at.
 */
const LABEL_MIN_PX = 26;
/**
 * ...except for this many of the most prominent, which keep their names
 * unconditionally. Zooming out should thin the labels, never empty the view.
 */
const LABEL_ALWAYS = 10;
/**
 * Cell size of the machinery occupancy map, in pixels. Coarse on purpose: it
 * answers "is there equipment roughly here?", and a finer grid would cost more
 * to build every frame without changing which seat wins.
 */
const LABEL_CELL = 14;
/**
 * What one seat of distance from the machine is worth, in units of covered
 * equipment. At this rate a plate that would bury a fifth of a vessel loses to
 * a seat four steps further out, but clear sky two steps away does not drag a
 * label off a machine that already had room above it.
 */
const LABEL_SEAT_COST = 0.05;
/**
 * Drawn, but not machinery: a zone outline painted on the floor, a text plate,
 * a flow arrow, a title block. A label over one of these hides nothing the
 * reader needs, so they are left out of the occupancy map.
 */
const LABEL_SEE_THROUGH = new Set(['zonebox', 'textlabel', 'processarrow', 'titleblock']);

/**
 * Candidate chip seats, as (lateral step, vertical step) from the seat
 * directly above the anchor. Tried in this order, so a label leaves the column
 * its equipment stands in only once that column is genuinely full — and when
 * it does it steps SIDEWAYS before it climbs. That is what stops a bank of
 * silos turning its names into a totem pole of text stacked over the silos.
 */
const LABEL_SEATS: readonly (readonly [number, number])[] = [
  [0, 0], [0, -1],
  [1, 0], [-1, 0],
  [1, -1], [-1, -1],
  [0, -2], [1, 1], [-1, 1],
  [1, -2], [-1, -2],
  [2, 0], [-2, 0],
  [2, -1], [-2, -1],
];

/** Resolved materials in the same index order the scene stores. */
const MATS: Material[] = MAT_KEYS.map((k) => MATERIALS[k] as Material);

// ── Clarity data: one packed word per triangle (kit spec §4.1) ──
// Edge k of a triangle runs v_k → v_(k+1)%3. The layout is owned by mesh.ts
// (the producer); only its flags and decoders are used here, so the two can
// never disagree about which bit means what.
/** Bits 0-2 of `featEdges`: FEATURE edge k — crease, rim, open boundary, joint. Always drawn. */
const F_FEATURE = F_E0 | F_E1 | F_E2;
/** Bits 3-5 of `featEdges`: SMOOTH edge k — drawn only where it is on the silhouette. */
const F_SMOOTH_SHIFT = 3;

/**
 * Feature-line style. One ink, mixed into the colour of the face the line
 * lies on, so a line reads as an edge of THAT surface — darker on a bright
 * vessel, near-black on a dark barrel — rather than as a wire drawn over the
 * plant. One mix for creases and silhouettes alike keeps it to a single
 * stroke per colour run.
 */
const INK: [number, number, number] = [0x0e, 0x14, 0x1c];
const INK_MIX = 0.6;
const LINE_W = 1;
/** A triangle smaller than this on screen draws no lines: they would be all the triangle was. */
const LINE_MIN_PX = 1.5;
/**
 * A face thinner than this on screen draws no lines of its own. Two 1 px lines
 * down a 2 px strip ARE the strip — a base rail or a flange edge seen from
 * across the plant turns into a dark hatched bar. Its broad neighbours still
 * draw the edges they share with it (a feature edge is flagged on both sides),
 * so the part keeps its outline and loses only the ink that swallowed it.
 */
const LINE_SLIVER_PX = 2.5;

/**
 * How thick a projected convex polygon is on screen, px: its area over its
 * longest edge (a triangle's height onto that edge counts double, so a
 * triangle and the quad it is half of read alike).
 */
function sliverPx(sx: Float32Array, off: number, pts: number): number {
  let a = 0;
  let e2 = 0;
  for (let k = 0; k < pts; k++) {
    const k1 = k + 1 === pts ? 0 : k + 1;
    const x0 = sx[off + k * 2];
    const y0 = sx[off + k * 2 + 1];
    const x1 = sx[off + k1 * 2];
    const y1 = sx[off + k1 * 2 + 1];
    a += x0 * y1 - x1 * y0;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const l = dx * dx + dy * dy;
    if (l > e2) e2 = l;
  }
  if (e2 <= 0) return 0;
  const area = Math.abs(a) / 2;
  return (pts === 3 ? 2 * area : area) / Math.sqrt(e2);
}
/**
 * An object smaller than this on screen keeps only its feature lines, and only
 * on its larger faces. At plant scale that is the difference between a crisp
 * outline and a machine drawn as a knot of ink.
 */
const LINE_SMALL_OBJ_PX = 28;
const LINE_SMALL_TRI_PX = 4;
/**
 * Only a face turned at least this far from the eye can own a silhouette edge.
 * A neighbour across a dihedral δ can face away only if this face's facing is
 * below sin δ, so the gate is set just above sin 45° — the coarsest wall an
 * 8-segment detail cylinder has — and the exact test below decides the rest.
 */
const SIL_FACING = 0.72;
/** Decal sort bias: this fraction of the owning object's bounding radius, capped. */
const DECAL_BIAS_K = 0.02;
const DECAL_BIAS_MAX = 1.5;

/** Line colour for each fill colour, memoised: a few hundred live entries a frame. */
const lineColorCache = new Map<number, string>();
function lineColor(packed: number): string {
  let s = lineColorCache.get(packed);
  if (s === undefined) {
    const r = Math.round(((packed >> 16) & 255) * (1 - INK_MIX) + INK[0] * INK_MIX);
    const g = Math.round(((packed >> 8) & 255) * (1 - INK_MIX) + INK[1] * INK_MIX);
    const b = Math.round((packed & 255) * (1 - INK_MIX) + INK[2] * INK_MIX);
    s = cssColor((r << 16) | (g << 8) | b);
    if (lineColorCache.size > 40000) lineColorCache.clear();
    lineColorCache.set(packed, s);
  }
  return s;
}

/** How near the cursor has to be to a dimension line to count as on it, px. */
const DIM_LINE_REACH = 7;

/** Distance from a point to a line segment, in screen pixels. */
function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const L = dx * dx + dy * dy;
  const t = L < 1e-9 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

export class Renderer3D {
  private ctx: CanvasRenderingContext2D | null = null;
  private W = 0;
  private H = 0;
  private dpr = 1;

  /** Shadow accumulation buffer, kept at half resolution — it gets blurred. */
  private shadowCanvas: HTMLCanvasElement | null = null;
  /**
   * The same layer, already blurred, and what it was blurred for.
   *
   * Shadows depend on the plant, the camera and the height slider — and on
   * nothing else. While the flow is running none of those move, so re-stamping
   * every hull and running a full-surface blur sixty times a second reproduces
   * the identical image sixty times a second. Keeping it is the difference
   * between a filter pass per frame and a plain blit.
   */
  private shadowBlur: HTMLCanvasElement | null = null;
  private shadowSig = '';
  private shadowScene: Scene | null = null;

  // Per-frame scratch, grown on demand and reused. Allocating these inside the
  // frame would hand the GC a few megabytes of garbage sixty times a second.
  private cap = 0;
  private sx = new Float32Array(0); // up to 4 screen points per triangle
  private np = new Uint8Array(0); // how many of them are used
  private col = new Int32Array(0);
  private alp = new Float32Array(0);
  private dep = new Float32Array(0);
  private objOf = new Int32Array(0);
  /**
   * Which polygon edges of each entry get a feature line this frame: bit k is
   * the edge from point k to point k+1 (wrapping). Zero on the moving frame.
   */
  private edg = new Uint8Array(0);
  private order = new Uint32Array(0);
  private scratch = new Uint32Array(0);
  private counts = new Uint32Array(BUCKETS + 1);
  private visible = 0;

  /**
   * Per-object bounding radius (world units, true height), for the decal bias.
   * A function of the scene alone, so it is worked out once per scene.
   */
  private objRad = new Float32Array(0);
  private objRadScene: Scene | null = null;
  /** Per-object screen diameter in px this frame — which objects are too small for lines. */
  private objPx = new Float32Array(0);

  // Separate, much smaller scratch for the handle overlay. It is drawn after
  // the plant with its own ordering so a gizmo is never buried inside the
  // machine it belongs to — which is precisely when it is needed.
  private ovCap = 0;
  private ovSx = new Float32Array(0);
  private ovNp = new Uint8Array(0);
  private ovCol = new Int32Array(0);
  private ovDep = new Float32Array(0);
  private ovObj = new Int32Array(0);
  private ovOrder: number[] = [];
  private ovN = 0;

  /** Triangles actually drawn last frame — the readout in the HUD. */
  drawn = 0;

  /**
   * Screen boxes of the hand-placed dimension labels, from the last frame.
   * Picking one is a plain rectangle test against these rather than a ray cast:
   * a dimension is a screen-space annotation, so it is selected the same way it
   * is drawn.
   */
  private dimHits: {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
    /**
     * The dimension LINE in screen space, for the hand-placed ones.
     * A figure is a twenty-pixel box; the line is the thing a person is
     * actually pointing at when they mean "that dimension", and on a busy
     * drawing it is the difference between reaching one and not.
     */
    seg?: [number, number, number, number];
  }[] = [];

  attach(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }

  resize(w: number, h: number, dpr: number): void {
    this.W = w;
    this.H = h;
    this.dpr = dpr;
  }

  private ensure(n: number): void {
    if (n <= this.cap) return;
    this.cap = Math.max(4096, n * 2);
    this.sx = new Float32Array(this.cap * 8);
    this.np = new Uint8Array(this.cap);
    this.col = new Int32Array(this.cap);
    this.alp = new Float32Array(this.cap);
    this.dep = new Float32Array(this.cap);
    this.objOf = new Int32Array(this.cap);
    this.edg = new Uint8Array(this.cap);
    this.order = new Uint32Array(this.cap);
    this.scratch = new Uint32Array(this.cap);
  }

  // ── Frame ──────────────────────────────────────────────────────────────

  render(scene: Scene, cam: Camera, o: RenderOptions, overlay?: Overlay): void {
    const ctx = this.ctx;
    if (!ctx || !this.W || !this.H) return;
    const { W, H, dpr } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.drawSky(ctx, W, H);

    const view = cam.view();
    const eye = cam.eye();
    const f = cam.scale(H);
    const persp = cam.kind === 'perspective';
    const cx = W / 2;
    const cy = H / 2;
    // In orthographic the depth divide is a constant, so one code path covers
    // both projections: `k` is pixels-per-world-unit at the sample's depth.
    const projK = (vz: number) => (persp ? f / Math.max(cam.near, -vz) : f / cam.distance);
    // A vertex is kept when its view-space z is <= clipZ. Orthographic has no
    // perspective divide and therefore no near plane to fall foul of, so its
    // clip value must ADMIT everything (+Infinity) — the mirror value here is
    // what silently culled the entire isometric view.
    const clipZ = persp ? -cam.near : Infinity;

    this.drawGround(ctx, scene, view, projK, clipZ, cx, cy, o.grid);
    if (o.shadows) this.drawShadows(ctx, scene, view, projK, clipZ, cx, cy, o);

    // ── Geometry ──
    const n = scene.count;
    this.ensure(n);
    const hs = o.heightScale;
    const isolating = o.isolate && o.selected.size > 0;
    const flow = o.flow ?? null;
    const pos = scene.pos;
    // The clarity word per triangle. Read defensively: a hand-built scene, or
    // one from a path that does not carry it, has none (or too few) — and then
    // there are simply no lines and no pairs, never a wrong one.
    const featArr = scene.feat;
    const feat = featArr && featArr.length >= n ? featArr : null;
    // Feature lines belong to the still frame, exactly like the seam seal: at
    // orbit speed they are noise that costs a second path per colour run.
    const lines = !!feat && o.seams !== false;
    if (feat) this.objectMetrics(scene, view, projK, clipZ, hs, lines);
    const objRad = this.objRad;
    const objPx = this.objPx;
    let vis = 0;
    let pairs = 0;
    let dMin = Infinity;
    let dMax = -Infinity;

    // View-space (vx..vz) and world-space (wx..wz, height-scaled) corners of
    // the current polygon: a triangle, or the four corners of a paired quad.
    const vx = [0, 0, 0, 0];
    const vy = [0, 0, 0, 0];
    const vz = [0, 0, 0, 0];
    const wx = [0, 0, 0, 0];
    const wy = [0, 0, 0, 0];
    const wz = [0, 0, 0, 0];

    for (let t = 0; t < n; t++) {
      const b = t * 9;
      // World → view. Height scaling is applied here rather than baked into
      // the scene so the slider is free.
      let behind = 0;
      for (let k = 0; k < 3; k++) {
        const i = b + k * 3;
        const px = pos[i];
        const py = pos[i + 1] * hs;
        const pz = pos[i + 2];
        wx[k] = px;
        wy[k] = py;
        wz[k] = pz;
        const X = view[0] * px + view[4] * py + view[8] * pz + view[12];
        const Y = view[1] * px + view[5] * py + view[9] * pz + view[13];
        const Z = view[2] * px + view[6] * py + view[10] * pz + view[14];
        vx[k] = X;
        vy[k] = Y;
        vz[k] = Z;
        if (Z > clipZ) behind++;
      }
      if (behind === 3) continue;

      const first = t;
      const f = feat ? feat[t] : 0;
      const ob = scene.obj[t];
      const matIdx = scene.mat[t];

      // ── Pair: this triangle (a,b,c) and the next (a,c,d) are one planar quad ──
      // Taken as one polygon only when the partner really is the other half
      // (its first two corners ARE a and c, bit for bit, same object and
      // material) and nothing needs near-plane clipping. Anything less and the
      // two halves go through separately, exactly as an unpaired scene would.
      let quad = false;
      let f2 = 0;
      if (f & F_PAIR && behind === 0 && t + 1 < n) {
        const q = b + 9;
        if (
          pos[q] === pos[b] && pos[q + 1] === pos[b + 1] && pos[q + 2] === pos[b + 2] &&
          pos[q + 3] === pos[b + 6] && pos[q + 4] === pos[b + 7] && pos[q + 5] === pos[b + 8] &&
          scene.obj[t + 1] === ob && scene.mat[t + 1] === matIdx
        ) {
          const px = pos[q + 6];
          const py = pos[q + 7] * hs;
          const pz = pos[q + 8];
          const Z = view[2] * px + view[6] * py + view[10] * pz + view[14];
          if (Z <= clipZ) {
            wx[3] = px;
            wy[3] = py;
            wz[3] = pz;
            vx[3] = view[0] * px + view[4] * py + view[8] * pz + view[12];
            vy[3] = view[1] * px + view[5] * py + view[9] * pz + view[13];
            vz[3] = Z;
            f2 = feat ? feat[t + 1] : 0;
            quad = true;
            // The partner is consumed HERE, so every early `continue` below
            // drops both halves together — they share one normal, one facing
            // and one screen box, so they always share one fate.
            t++;
          }
        }
      }
      const corners = quad ? 4 : 3;

      // Shading normal: the mean of the three vertex normals. Facet-normal
      // meshes carry the same normal on all three, so this is exact for them
      // and still a fair average for a legacy smooth-normal mesh.
      let nx = scene.nrm[b] + scene.nrm[b + 3] + scene.nrm[b + 6];
      let ny = scene.nrm[b + 1] + scene.nrm[b + 4] + scene.nrm[b + 7];
      let nz = scene.nrm[b + 2] + scene.nrm[b + 5] + scene.nrm[b + 8];
      const nl = Math.hypot(nx, ny, nz);
      if (nl < 1e-6) continue;
      nx /= nl;
      ny /= nl;
      nz /= nl;
      if (hs !== 1) {
        // Non-uniform Y scaling shears the normals; correct them or a stretched
        // silo lights like an unstretched one.
        ny /= hs;
        const l2 = Math.hypot(nx, ny, nz) || 1;
        nx /= l2;
        ny /= l2;
        nz /= l2;
      }

      const m = MATS[matIdx];

      // Backface cull against the AUTHORED normal, not the winding: every
      // primitive builder emits outward normals, and relying on those removes a
      // whole class of winding bugs the models would otherwise have to avoid.
      let c0x = wx[0] + wx[1] + wx[2];
      let c0y = wy[0] + wy[1] + wy[2];
      let c0z = wz[0] + wz[1] + wz[2];
      if (quad) {
        c0x = (c0x + wx[3]) / 4;
        c0y = (c0y + wy[3]) / 4;
        c0z = (c0z + wz[3]) / 4;
      } else {
        c0x /= 3;
        c0y /= 3;
        c0z /= 3;
      }
      let ex = eye.x - c0x;
      let ey = eye.y - c0y;
      let ez = eye.z - c0z;
      const el = Math.hypot(ex, ey, ez) || 1;
      ex /= el;
      ey /= el;
      ez /= el;
      let facing = nx * ex + ny * ey + nz * ez;
      let flipped = false;
      if (facing < 0) {
        if (!m.doubleSided) continue;
        // Show the inside of open vessels by flipping the normal to the eye.
        nx = -nx;
        ny = -ny;
        nz = -nz;
        facing = -facing;
        flipped = true;
      }

      // Clip against the near plane, then project. The clip yields 3 or 4
      // points; canvas fills either without a second triangulation step. A
      // paired quad is never clipped (it was split back into halves above).
      let pts = 0;
      const off = vis * 8;
      if (behind === 0) {
        for (let k = 0; k < corners; k++) {
          const kk = projK(vz[k]);
          this.sx[off + pts * 2] = cx + vx[k] * kk;
          this.sx[off + pts * 2 + 1] = cy - vy[k] * kk;
          pts++;
        }
      } else {
        for (let k = 0; k < 3; k++) {
          const j = (k + 1) % 3;
          const kIn = vz[k] <= clipZ;
          const jIn = vz[j] <= clipZ;
          if (kIn) {
            const kk = projK(vz[k]);
            this.sx[off + pts * 2] = cx + vx[k] * kk;
            this.sx[off + pts * 2 + 1] = cy - vy[k] * kk;
            pts++;
          }
          if (kIn !== jIn && pts < 4) {
            const s = (clipZ - vz[k]) / (vz[j] - vz[k]);
            const ix = vx[k] + (vx[j] - vx[k]) * s;
            const iy = vy[k] + (vy[j] - vy[k]) * s;
            const kk = projK(clipZ);
            this.sx[off + pts * 2] = cx + ix * kk;
            this.sx[off + pts * 2 + 1] = cy - iy * kk;
            pts++;
          }
        }
        if (pts < 3) continue;
      }

      // Cheap screen-space reject: anything entirely off-canvas costs nothing
      // further. Worth doing before shading, which is the expensive part.
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      for (let k = 0; k < pts; k++) {
        const X = this.sx[off + k * 2];
        const Y = this.sx[off + k * 2 + 1];
        if (X < minX) minX = X;
        if (X > maxX) maxX = X;
        if (Y < minY) minY = Y;
        if (Y > maxY) maxY = Y;
      }
      if (maxX < -8 || minX > W + 8 || maxY < -8 || minY > H + 8) continue;
      // Sub-pixel triangles are invisible but cost a full path each.
      if (maxX - minX < 0.22 && maxY - minY < 0.22) continue;

      const so = scene.objects[ob];
      let tint = so?.tint ?? null;
      let tintAmt = so?.tintAmt ?? 0;

      // ── The plant running ──
      // A slug is not extra geometry drawn over the pipe; it is the pipe,
      // shaded differently for as long as the slug is passing this triangle.
      // That is what makes it disappear correctly behind a silo, catch the key
      // light down the top of a horizontal run, and cost nothing but the two
      // array reads below. (A paired band takes its first half's distance; the
      // two halves of one band sit at the same place along the run.)
      if (flow && so?.flow && so.flow !== 'none') {
        const s = scene.flowS[first];
        if (s >= 0) {
          const st = so.flow === 'air' ? FLOW_STYLES.air : FLOW_STYLES.material;
          // A run whose contents the balance can name moves in that material's
          // own colour; everything else keeps the medium's. The RHYTHM is still
          // the medium's either way, so material and air stay tellable apart
          // without relying on colour at all.
          const col = so.flowCol ?? st.col;
          const w = slugAt(st, s, flow.travel, !!so.flowRev);
          if (w > 0.004) {
            // Composited over whatever paint the run already carries, rather
            // than replacing it — a line colour-coded green is still green
            // between its slugs, which is the point of having coloured it.
            const a = w * st.strength;
            const keep = tintAmt * (1 - a);
            const amt = keep + a;
            for (let c = 0; c < 3; c++) {
              const base = m.albedo[c];
              const own = tint ? tint[c] : base;
              FLOW_TINT[c] = base + ((own - base) * keep + (col[c] - base) * a) / amt;
            }
            tint = FLOW_TINT;
            tintAmt = amt;
          }
        }
      }

      if (so) {
        if (o.selected.has(so.id)) {
          tint = SELECT_TINT;
          tintAmt = 0.5;
        } else if (o.hoveredId === so.id) {
          tint = HOVER_TINT;
          tintAmt = 0.3;
        }
      }

      let alpha = m.alpha ?? 1;
      if (isolating && (!so || !o.selected.has(so.id))) {
        // Faint enough that a wall of ghosted pipework still reads as see-
        // through once the painter's sort has stacked a few hundred of them.
        alpha *= 0.07;
        tint = null;
        tintAmt = 0;
      }

      // ── Feature lines for this polygon ──
      // Only on the still frame, only on opaque paint (a line round a pane of
      // glass or a ghosted machine would make it look solid again), never on a
      // clipped triangle (its clip edge is not an edge of anything), and never
      // on a face too small to carry one.
      let em = 0;
      if (lines && behind === 0 && alpha >= 0.9 && m.lines !== false) {
        const bw = maxX - minX;
        const bh = maxY - minY;
        const big = bw > bh ? bw : bh;
        if (big >= LINE_MIN_PX) {
          // Map the authored triangle edges onto this polygon's edges. For a
          // quad (a,b,c)+(a,c,d): ab = t.e0, bc = t.e1, cd = (t+1).e1,
          // da = (t+1).e2; the shared diagonal a→c is never an edge.
          const e1 = featEdges(f);
          let fe: number;
          let sm: number;
          if (quad) {
            const e2 = featEdges(f2);
            fe = (e1 & 3) | (((e2 >> 1) & 3) << 2);
            sm = ((e1 >> F_SMOOTH_SHIFT) & 3) | (((e2 >> (F_SMOOTH_SHIFT + 1)) & 3) << 2);
          } else {
            fe = e1 & F_FEATURE;
            sm = (e1 >> F_SMOOTH_SHIFT) & 7;
          }
          if (objPx[ob] < LINE_SMALL_OBJ_PX) {
            sm = 0;
            if (big < LINE_SMALL_TRI_PX) fe = 0;
          }
          // A sliver keeps its silhouette test (the grazing band of a vessel
          // IS the thin one, and its outer edge is the outline) but gives up
          // its feature edges to its broader neighbours.
          if (fe && sliverPx(this.sx, off, pts) < LINE_SLIVER_PX) fe = 0;
          em = fe;
          // A smooth edge is on the silhouette when the face beyond it turns
          // away from the eye. Its normal is n·cos δ + s·sin δ, with s the
          // outward in-plane direction across the edge — taken from the
          // geometry (pointing away from the centroid) rather than from the
          // winding, so a mesh wound either way gets the same answer.
          if (sm && !flipped && facing < SIL_FACING) {
            for (let k = 0; k < corners; k++) {
              if (!(sm & (1 << k)) || fe & (1 << k)) continue;
              // Dihedral of this polygon edge: a quad's cd and da are the
              // second triangle's edges 1 and 2.
              const dih =
                quad && k >= 2 ? featDih(f2, (k - 1) as 1 | 2) : featDih(f, k as 0 | 1 | 2);
              if (!dih) continue;
              const k1 = k + 1 === corners ? 0 : k + 1;
              const exk = wx[k1] - wx[k];
              const eyk = wy[k1] - wy[k];
              const ezk = wz[k1] - wz[k];
              let ox = eyk * nz - ezk * ny;
              let oy = ezk * nx - exk * nz;
              let oz = exk * ny - eyk * nx;
              const sl = Math.hypot(ox, oy, oz);
              if (sl < 1e-9) continue;
              const mx = (wx[k] + wx[k1]) / 2 - c0x;
              const my = (wy[k] + wy[k1]) / 2 - c0y;
              const mz = (wz[k] + wz[k1]) / 2 - c0z;
              if (ox * mx + oy * my + oz * mz < 0) {
                ox = -ox;
                oy = -oy;
                oz = -oz;
              }
              const sE = (ox * ex + oy * ey + oz * ez) / sl;
              if (Math.cos(dih) * facing + Math.sin(dih) * sE <= 0) em |= 1 << k;
            }
          }
        }
      }

      this.np[vis] = pts;
      this.edg[vis] = em;
      this.col[vis] = shade(v3(nx, ny, nz), v3(ex, ey, ez), m, tint, tintAmt);
      this.alp[vis] = alpha;
      let d = quad
        ? -(vz[0] + vz[1] + vz[2] + vz[3]) / 4
        : -(vz[0] + vz[1] + vz[2]) / 3;
      // A plate, screen or grille stands a hair proud of its host. Sorting it
      // a little toward the eye — in proportion to the object, so it never
      // jumps in front of a neighbouring machine — keeps it on top of the
      // face it is fixed to from every angle.
      if (f & F_DECAL) {
        const bias = objRad[ob] * DECAL_BIAS_K;
        d -= bias < DECAL_BIAS_MAX ? bias : DECAL_BIAS_MAX;
      }
      this.dep[vis] = d;
      this.objOf[vis] = ob;
      if (d < dMin) dMin = d;
      if (d > dMax) dMax = d;
      vis++;
      if (quad) pairs++;
    }
    this.visible = vis;
    this.drawn = vis + pairs;

    this.sortByDepth(vis, dMin, dMax);
    this.paint(ctx, vis, o.seams, lines);

    if (overlay && overlay.count) this.paintOverlay(ctx, overlay, view, projK, clipZ, cx, cy, eye);
    else this.ovN = 0;

    // Both dimension passes fill the same pick list, so the frame clears it —
    // not the second pass, which used to wipe the first pass's boxes.
    this.dimHits.length = 0;
    if (o.dims) this.drawDimensions(ctx, scene, view, projK, clipZ, cx, cy, o);
    this.drawUserDimensions(ctx, scene, view, projK, clipZ, cx, cy, o);
    if (o.floors) this.drawFloorDatum(ctx, scene, view, projK, clipZ, cx, cy, o);
    if (o.labels) this.drawLabels(ctx, scene, view, projK, clipZ, cx, cy, o);
    // Last, so the number being watched is never behind a label or a chip.
    this.drawDraftLengths(ctx, view, projK, clipZ, cx, cy, o);
  }

  /**
   * Bucket sort on depth, far to near.
   * A comparator sort of twenty thousand triangles costs several milliseconds
   * every frame; bucketing is linear and, at 4096 buckets across the scene's
   * depth range, indistinguishable in the result.
   */
  private sortByDepth(n: number, dMin: number, dMax: number): void {
    if (n === 0) return;
    const B = BUCKETS;
    const span = dMax - dMin;
    if (!(span > 1e-6)) {
      for (let i = 0; i < n; i++) this.order[i] = i;
      return;
    }
    const k = (B - 1) / span;
    // Reused, not reallocated: a fresh 64KB array sixty times a second is pure
    // garbage-collector pressure for no benefit.
    const counts = this.counts;
    counts.fill(0);
    const bucket = this.scratch;
    for (let i = 0; i < n; i++) {
      // Invert so bucket 0 is the FARTHEST — painting then runs 0 → n.
      const b = B - 1 - ((this.dep[i] - dMin) * k) | 0;
      const bb = b < 0 ? 0 : b > B - 1 ? B - 1 : b;
      bucket[i] = bb;
      counts[bb + 1]++;
    }
    for (let b = 0; b < B; b++) counts[b + 1] += counts[b];
    for (let i = 0; i < n; i++) this.order[counts[bucket[i]]++] = i;
  }

  /**
   * Per-object figures the triangle loop needs: the bounding radius (for the
   * decal bias; a function of the scene alone, so kept until the scene is
   * rebuilt) and, when lines are being drawn, the object's diameter on screen
   * this frame (which objects are too small to carry more than an outline).
   */
  private objectMetrics(
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    hs: number,
    lines: boolean,
  ): void {
    const objs = scene.objects;
    const no = objs.length;
    if (this.objRadScene !== scene || this.objRad.length < no) {
      if (this.objRad.length < no) this.objRad = new Float32Array(Math.max(64, no * 2));
      for (let i = 0; i < no; i++) {
        const so = objs[i];
        this.objRad[i] = 0.5 * Math.hypot(so.max.x - so.min.x, so.max.y - so.min.y, so.max.z - so.min.z);
      }
      this.objRadScene = scene;
    }
    if (!lines) return;
    if (this.objPx.length < no) this.objPx = new Float32Array(Math.max(64, no * 2));
    for (let i = 0; i < no; i++) {
      const so = objs[i];
      const x = (so.min.x + so.max.x) / 2;
      const y = ((so.min.y + so.max.y) / 2) * hs;
      const z = (so.min.z + so.max.z) / 2;
      const r = 0.5 * Math.hypot(so.max.x - so.min.x, (so.max.y - so.min.y) * hs, so.max.z - so.min.z);
      const Z = view[2] * x + view[6] * y + view[10] * z + view[14];
      // An object whose centre is behind the near plane is one the camera is
      // standing in: as large as anything can be.
      this.objPx[i] = Z > clipZ ? Infinity : 2 * r * projK(Z);
    }
  }

  /**
   * Fill the sorted triangles, batching runs that share a colour and alpha;
   * then, on the still frame, stroke each run's feature lines.
   *
   * The lines of a run are stroked straight after its own fill and seal, so
   * the painter's sort occludes them exactly as it occludes the faces: a
   * nearer run painted later covers them. An edge shared by two faces is
   * flagged on both, so whichever face is painted last redraws it in full.
   */
  private paint(ctx: CanvasRenderingContext2D, n: number, seams = true, lines = false): void {
    if (!n) return;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = 0.8;
    const order = this.order;
    const sx = this.sx;
    const np = this.np;
    const edg = this.edg;
    let curCol = -1;
    let curAlp = -1;
    let open = false;
    /** First entry (in paint order) of the current run, and whether any of it has lines. */
    let runStart = 0;
    let runLines = false;
    const flush = (end: number) => {
      if (!open) return;
      const s = cssColor(curCol);
      ctx.fillStyle = s;
      ctx.fill();
      // Stroking in the fill colour seals the antialiased hairline that would
      // otherwise show between every pair of adjacent triangles. Skipped on
      // near-transparent runs, where the extra coverage would just make a
      // ghosted object look solid again — and skipped entirely while the
      // camera is moving, where it is the single most expensive thing on
      // screen and the least visible.
      if (seams && curAlp > 0.12) {
        ctx.strokeStyle = s;
        ctx.stroke();
      }
      if (runLines) {
        // One path for the whole run. Consecutive flagged edges of a polygon
        // are joined into one polyline, so a box face outlined on three sides
        // gets round joins instead of three overlapping caps.
        ctx.beginPath();
        for (let j = runStart; j < end; j++) {
          const t = order[j];
          const em = edg[t];
          if (!em) continue;
          const off = t * 8;
          const pts = np[t];
          let pen = false;
          for (let k = 0; k < pts; k++) {
            if (em & (1 << k)) {
              if (!pen) ctx.moveTo(sx[off + k * 2], sx[off + k * 2 + 1]);
              const k1 = k + 1 === pts ? 0 : k + 1;
              ctx.lineTo(sx[off + k1 * 2], sx[off + k1 * 2 + 1]);
              pen = true;
            } else pen = false;
          }
        }
        ctx.strokeStyle = lineColor(curCol);
        ctx.lineWidth = LINE_W;
        ctx.stroke();
        ctx.lineWidth = 0.8;
      }
      open = false;
    };
    for (let i = 0; i < n; i++) {
      const t = order[i];
      const c = this.col[t];
      const a = this.alp[t];
      if (c !== curCol || a !== curAlp) {
        flush(i);
        curCol = c;
        curAlp = a;
        ctx.globalAlpha = a;
        ctx.beginPath();
        open = true;
        runStart = i;
        runLines = false;
      }
      const off = t * 8;
      const pts = np[t];
      ctx.moveTo(sx[off], sx[off + 1]);
      for (let k = 1; k < pts; k++) ctx.lineTo(sx[off + k * 2], sx[off + k * 2 + 1]);
      ctx.closePath();
      if (lines && edg[t]) runLines = true;
    }
    flush(n);
    ctx.globalAlpha = 1;
    ctx.lineCap = 'butt';
  }

  // ── Background passes ──────────────────────────────────────────────────

  private drawSky(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#e8edf4');
    g.addColorStop(0.55, '#dbe2ec');
    g.addColorStop(1, '#c6cedb');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  /** Project a world point, returning null when it falls behind the near plane. */
  private proj(
    p: Vec3,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
  ): { x: number; y: number; z: number } | null {
    const q = xfPoint(view as unknown as Float64Array, p);
    if (q.z > clipZ) return null;
    const k = projK(q.z);
    return { x: cx + q.x * k, y: cy - q.y * k, z: q.z };
  }

  /** The plant floor: a shaded slab with a grid that fades toward its edges. */
  private drawGround(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    grid: boolean,
  ): void {
    const pad = 320;
    const x0 = scene.min.x - pad;
    const x1 = scene.max.x + pad;
    const z0 = scene.min.z - pad;
    const z1 = scene.max.z + pad;

    // Slab. Clipped against the near plane so a low camera doesn't smear it.
    const quad = [v3(x0, 0, z0), v3(x1, 0, z0), v3(x1, 0, z1), v3(x0, 0, z1)];
    const poly = this.clipPolyToScreen(quad, view, projK, clipZ, cx, cy);
    if (poly.length >= 3) {
      ctx.fillStyle = '#b9bfc8';
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      ctx.fill();
    }
    if (!grid) return;

    // Grid. Spacing is chosen so the count stays bounded however far you zoom
    // out — the alternative is tens of thousands of invisible line segments.
    const step = this.gridStep(Math.max(x1 - x0, z1 - z0));
    ctx.lineWidth = 1;
    const drawLines = (along: 'x' | 'z') => {
      const a0 = along === 'x' ? x0 : z0;
      const a1 = along === 'x' ? x1 : z1;
      const s = Math.ceil(a0 / step) * step;
      for (let a = s; a <= a1; a += step) {
        const major = Math.abs(a % (step * 5)) < 1e-6;
        const p1 = along === 'x' ? v3(a, 0, z0) : v3(x0, 0, a);
        const p2 = along === 'x' ? v3(a, 0, z1) : v3(x1, 0, a);
        const seg = this.clipPolyToScreen([p1, p2], view, projK, clipZ, cx, cy, true);
        if (seg.length < 2) continue;
        ctx.strokeStyle = major ? 'rgba(70,80,95,0.30)' : 'rgba(70,80,95,0.14)';
        ctx.beginPath();
        ctx.moveTo(seg[0].x, seg[0].y);
        ctx.lineTo(seg[1].x, seg[1].y);
        ctx.stroke();
      }
    };
    drawLines('x');
    drawLines('z');
  }

  private gridStep(span: number): number {
    const target = span / 42;
    const pow = Math.pow(10, Math.floor(Math.log10(Math.max(1, target))));
    for (const m of [1, 2, 5, 10]) if (pow * m >= target) return pow * m;
    return pow * 10;
  }

  /**
   * Clip a world-space polygon (or a 2-point segment) against the near plane
   * and project what survives. Sutherland-Hodgman, in view space.
   */
  private clipPolyToScreen(
    pts: Vec3[],
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    isSegment = false,
  ): { x: number; y: number }[] {
    const vs = pts.map((p) => xfPoint(view, p));
    const out: { x: number; y: number; z: number }[] = [];
    const n = vs.length;
    const last = isSegment ? n - 1 : n;
    for (let i = 0; i < last; i++) {
      const a = vs[i];
      const b = vs[(i + 1) % n];
      const aIn = a.z <= clipZ;
      const bIn = b.z <= clipZ;
      if (aIn) out.push(a);
      if (aIn !== bIn) {
        const s = (clipZ - a.z) / (b.z - a.z);
        out.push({ x: a.x + (b.x - a.x) * s, y: a.y + (b.y - a.y) * s, z: clipZ });
      }
    }
    if (isSegment && vs[n - 1].z <= clipZ && out.length < 2) out.push(vs[n - 1]);
    return out.map((q) => {
      const k = projK(q.z);
      return { x: cx + q.x * k, y: cy - q.y * k };
    });
  }

  /**
   * Soft ground shadows.
   *
   * Each object's shadow is the exact silhouette of a prism: the convex hull of
   * its footprint together with that footprint slid along the light direction
   * by its own height. Every shadow is stamped OPAQUE onto a half-resolution
   * offscreen canvas and the whole layer is composited once, blurred — stamping
   * them straight onto the frame would double the darkness wherever two
   * overlapped, which is the giveaway of a fake shadow.
   *
   * The hulls are cached: an object's shadow outline depends on its footprint,
   * its height and the vertical exaggeration — never on the camera — so
   * rebuilding one per object per frame was work thrown away on every orbit.
   * A WeakMap keyed by the object means a rebuilt scene drops the stale
   * entries with no bookkeeping of its own.
   */
  private hulls = new WeakMap<SceneObject, { hs: number; hull: Vec2[] }>();

  private drawShadows(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    const casters = scene.objects.filter((ob) => ob.shadow.length >= 3);
    if (!casters.length) return;
    const sw = Math.max(1, Math.round(this.W / 2));
    const sh = Math.max(1, Math.round(this.H / 2));

    // Everything the shadows are a function of. `projK` is sampled rather than
    // inspected so one signature covers both projections, and the scene is
    // compared by identity — it is replaced wholesale on every rebuild.
    const sig = `${sw}x${sh}|${o.heightScale}|${cx},${cy},${clipZ}|${projK(-100)}|${view.join()}`;
    if (this.shadowBlur && this.shadowSig === sig && this.shadowScene === scene) {
      this.blitShadows(ctx);
      return;
    }
    this.shadowSig = sig;
    this.shadowScene = scene;

    let sc = this.shadowCanvas;
    if (!sc) {
      sc = document.createElement('canvas');
      this.shadowCanvas = sc;
    }
    if (sc.width !== sw || sc.height !== sh) {
      sc.width = sw;
      sc.height = sh;
    }
    const sctx = sc.getContext('2d');
    if (!sctx) return;
    sctx.setTransform(1, 0, 0, 1, 0, 0);
    sctx.clearRect(0, 0, sw, sh);
    sctx.fillStyle = '#000';

    // Ground displacement per unit of height, along the light.
    const L = KEY_DIR;
    const kx = -L.x / Math.max(0.25, L.y);
    const kz = -L.z / Math.max(0.25, L.y);

    for (const ob of casters) {
      let cached = this.hulls.get(ob);
      if (!cached || cached.hs !== o.heightScale) {
        // An item on an upper level casts from its own base, not from the
        // ground: the whole shadow slides along the light, it doesn't stretch
        // from zero.
        const yA = ob.shadowY0 * o.heightScale;
        const yB = ob.shadowY1 * o.heightScale;
        const pts: Vec2[] = [];
        for (const q of ob.shadow) {
          pts.push({ x: q.x + kx * yA, y: q.y + kz * yA });
          pts.push({ x: q.x + kx * yB, y: q.y + kz * yB });
        }
        cached = { hs: o.heightScale, hull: convexHull(pts) };
        this.hulls.set(ob, cached);
      }
      const hull = cached.hull;
      if (hull.length < 3) continue;
      const poly = this.clipPolyToScreen(
        hull.map((q) => v3(q.x, 0, q.y)),
        view,
        projK,
        clipZ,
        cx,
        cy,
      );
      if (poly.length < 3) continue;
      sctx.beginPath();
      sctx.moveTo(poly[0].x / 2, poly[0].y / 2);
      for (let i = 1; i < poly.length; i++) sctx.lineTo(poly[i].x / 2, poly[i].y / 2);
      sctx.closePath();
      sctx.fill();
    }

    // Blur once, into a layer of its own, rather than on the way to the screen.
    // The radius halves with the resolution: this is stamped at half size and
    // blown up to full on the way out, so 1.5 here lands as the same 3 pixels
    // of softness it always was.
    let blur = this.shadowBlur;
    if (!blur) {
      blur = document.createElement('canvas');
      this.shadowBlur = blur;
    }
    if (blur.width !== sw || blur.height !== sh) {
      blur.width = sw;
      blur.height = sh;
    }
    const bctx = blur.getContext('2d');
    if (!bctx) return;
    bctx.setTransform(1, 0, 0, 1, 0, 0);
    bctx.clearRect(0, 0, sw, sh);
    bctx.filter = 'blur(1.5px)';
    bctx.drawImage(sc, 0, 0);
    bctx.filter = 'none';

    this.blitShadows(ctx);
  }

  /** Composite the kept shadow layer. The only part of the pass done per frame. */
  private blitShadows(ctx: CanvasRenderingContext2D): void {
    const blur = this.shadowBlur;
    if (!blur) return;
    ctx.save();
    ctx.globalAlpha = 0.26;
    ctx.drawImage(blur, 0, 0, this.W, this.H);
    ctx.restore();
  }

  // ── Labels ─────────────────────────────────────────────────────────────

  /**
   * Floating equipment labels: a frosted chip, an anchor dot on the machine it
   * names, and a dog-leg leader between the two.
   *
   * The pass exists to NAME the plant, not to hide it, and every rule below
   * follows from that:
   *   • OCCLUSION-AWARE SEATING. A coarse occupancy map of the machinery is
   *     built first, so each candidate seat can be scored by how much equipment
   *     its plate would cover. Labels therefore migrate into the sky and into
   *     the gaps between vessels of their own accord, instead of landing on the
   *     first free spot and burying whatever happened to be there.
   *   • THE PLATE EARNS ITS PLACE. Its opacity follows what is actually behind
   *     it: over open sky it fades to a whisper, over dark steel it goes solid.
   *     A label never asks for more cover than its background demands.
   *   • PRIORITY BY PROMINENCE, not by depth. The pass is a competition for
   *     screen space and the winner should be the machine the reader is looking
   *     at. Ordering far-to-near — exactly right for the painter's sort on
   *     triangles — let a speck at the back take the seat over a foreground
   *     vessel.
   *   • DOG-LEG SEATS. A blocked label steps sideways before it climbs, so a
   *     cluster fans its names out instead of stacking them into a column.
   *   • SIZE TIERS. Below a fraction of the largest item on screen a label
   *     dims; below an absolute pixel size it drops out. Selection and hover
   *     always override, and the leading handful are never dropped, so zooming
   *     out thins the labels rather than emptying the view.
   */
  private drawLabels(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    interface Box {
      x0: number;
      y0: number;
      x1: number;
      y1: number;
    }

    /** Screen bounding box of an object, from its eight world corners. */
    const boxOf = (ob: SceneObject): Box | null => {
      let x0 = Infinity;
      let x1 = -Infinity;
      let y0 = Infinity;
      let y1 = -Infinity;
      for (let c = 0; c < 8; c++) {
        const q = this.proj(
          v3(
            c & 1 ? ob.max.x : ob.min.x,
            (c & 2 ? ob.max.y : ob.min.y) * o.heightScale,
            c & 4 ? ob.max.z : ob.min.z,
          ),
          view,
          projK,
          clipZ,
          cx,
          cy,
        );
        if (!q) continue;
        if (q.x < x0) x0 = q.x;
        if (q.x > x1) x1 = q.x;
        if (q.y < y0) y0 = q.y;
        if (q.y > y1) y1 = q.y;
      }
      return x0 > x1 ? null : { x0, y0, x1, y1 };
    };

    // ── Where the machinery is ──────────────────────────────────────────────
    // A coarse occupancy grid, summed into an area table so that "how much
    // equipment sits under this rectangle?" costs four lookups per candidate
    // seat rather than a scan.
    //
    // Only the compact solids go in. A pipe's bounding box is mostly empty air
    // — a diagonal run would mark a huge rectangle of nothing as occupied and
    // push labels away from the clearest sky on screen — and covering a thin
    // pipe matters far less than covering the machine it feeds.
    const GW = Math.max(1, Math.ceil(this.W / LABEL_CELL));
    const GH = Math.max(1, Math.ceil(this.H / LABEL_CELL));
    const cells = new Float32Array(GW * GH);
    const boxes = new Map<string, Box>();
    for (const ob of scene.objects) {
      const b = boxOf(ob);
      if (!b) continue;
      if (ob.label || ob.tag) boxes.set(ob.id, b);
      // Only the solid machines go in. A pipe's box is mostly empty air, the
      // steelwork's box is the whole plant footprint, and a zone outline or a
      // text plate is an annotation on the floor rather than something a reader
      // loses by having it covered. Counting any of them marks most of the
      // frame as occupied and leaves the score nothing to discriminate on —
      // which is exactly how this pass first measured a 0% improvement.
      if (ob.kind !== 'shape' || LABEL_SEE_THROUGH.has(ob.type)) continue;
      const gx0 = Math.max(0, Math.floor(b.x0 / LABEL_CELL));
      const gx1 = Math.min(GW - 1, Math.floor(b.x1 / LABEL_CELL));
      const gy0 = Math.max(0, Math.floor(b.y0 / LABEL_CELL));
      const gy1 = Math.min(GH - 1, Math.floor(b.y1 / LABEL_CELL));
      for (let gy = gy0; gy <= gy1; gy++)
        for (let gx = gx0; gx <= gx1; gx++) {
          // Set, not add: two machines behind one another are no worse to
          // cover than one, and a bounded cell keeps the score a clean
          // fraction of the plate's area.
          cells[gy * GW + gx] = 1;
        }
    }
    // Summed-area table, one row and column of zeros on the near side.
    const sat = new Float32Array((GW + 1) * (GH + 1));
    for (let gy = 0; gy < GH; gy++)
      for (let gx = 0; gx < GW; gx++)
        sat[(gy + 1) * (GW + 1) + gx + 1] =
          cells[gy * GW + gx] +
          sat[gy * (GW + 1) + gx + 1] +
          sat[(gy + 1) * (GW + 1) + gx] -
          sat[gy * (GW + 1) + gx];

    /** Fraction of a screen rectangle that has machinery behind it, 0..1. */
    const coverage = (x0: number, y0: number, x1: number, y1: number): number => {
      const a = Math.max(0, Math.min(GW, Math.floor(x0 / LABEL_CELL)));
      const b = Math.max(0, Math.min(GW, Math.ceil(x1 / LABEL_CELL)));
      const c = Math.max(0, Math.min(GH, Math.floor(y0 / LABEL_CELL)));
      const d = Math.max(0, Math.min(GH, Math.ceil(y1 / LABEL_CELL)));
      const n = (b - a) * (d - c);
      if (n <= 0) return 0;
      const s =
        sat[d * (GW + 1) + b] - sat[c * (GW + 1) + b] - sat[d * (GW + 1) + a] + sat[c * (GW + 1) + a];
      return s / n;
    };

    // ── The candidates ──────────────────────────────────────────────────────
    interface Item {
      ob: SceneObject;
      /** Anchor: where the leader touches the equipment. */
      ax: number;
      ay: number;
      /** On-screen size in pixels — decides the seat contest and the tier. */
      prom: number;
      sel: boolean;
      /** Selected or hovered: never dimmed, never dropped, seated first. */
      forced: boolean;
    }
    const items: Item[] = [];
    for (const ob of scene.objects) {
      if (!ob.label && !(o.tags && ob.tag)) continue;
      if (ob.kind === 'conn' && !ob.label) continue;
      const b = boxes.get(ob.id);
      if (!b) continue;
      // Visibility is a property of the MACHINE, not of its label anchor. The
      // anchor sits on top of the object, and on a tall vessel that top leaves
      // the frame long before the vessel does — culling on it made the silo
      // filling your screen the one thing without a name.
      if (b.x1 < 0 || b.x0 > this.W || b.y1 < 0 || b.y0 > this.H) continue;
      const q = this.proj(
        v3(ob.labelAt.x, ob.labelAt.y * o.heightScale, ob.labelAt.z),
        view,
        projK,
        clipZ,
        cx,
        cy,
      );
      if (!q) continue;
      // Prominence, measured rather than estimated: the screen diagonal is what
      // the reader actually sees, where a world diagonal scaled by depth was
      // only a proxy for it.
      let prom = Math.hypot(b.x1 - b.x0, b.y1 - b.y0);
      // A pipe run is long, so its diagonal flatters it: one line across the
      // plant would otherwise outrank every machine it connects.
      if (ob.kind === 'conn' || ob.kind === 'pipe3') prom *= 0.5;
      // Pull the anchor back onto whatever part of the object is on screen, so
      // the leader still touches the machine it names.
      const sel = o.selected.has(ob.id);
      items.push({
        ob,
        ax: Math.min(Math.max(q.x, Math.max(b.x0, 2)), Math.min(b.x1, this.W - 2)),
        ay: Math.min(Math.max(q.y, Math.max(b.y0, 2)), Math.min(b.y1, this.H - 2)),
        prom,
        sel,
        forced: sel || o.hoveredId === ob.id,
      });
    }
    if (!items.length) return;

    // Most prominent first, so it places first and wins the contested seat.
    items.sort((a, b) => (b.forced ? 1 : 0) - (a.forced ? 1 : 0) || b.prom - a.prom);
    let promMax = 0;
    for (const it of items) if (it.prom > promMax) promMax = it.prom;

    interface Chip {
      it: Item;
      /** Chip centre X, chip top Y. */
      x: number;
      y: number;
      w: number;
      h: number;
      label: string;
      tag: string;
      font: string;
      alpha: number;
      /** Signed pixel offset from the anchor column; 0 means straight above. */
      lateral: number;
      /** How much machinery this plate ends up covering, 0..1. */
      cover: number;
    }
    const chips: Chip[] = [];
    const placed: { x: number; y: number; w: number; h: number }[] = [];

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const rel = promMax > 0 ? it.prom / promMax : 1;
      let alpha = 1;
      if (!it.forced) {
        // Two different questions, so two different measures.
        //
        // ABSOLUTE pixels decide whether an item has earned a name at all: a
        // valve rendered twenty pixels across cannot carry a hundred-pixel
        // plate, whatever else is on screen. This is the gate that thins the
        // view as the camera pulls back — but never strips it bare, because
        // the leading handful are guaranteed their names.
        //
        // RELATIVE size decides how loudly it says it. Being a ratio it barely
        // moves with zoom, which is the point: the visual hierarchy holds still
        // while you navigate instead of flickering between tiers.
        if (it.prom < LABEL_MIN_PX && i >= LABEL_ALWAYS) continue;
        const byRel = rel < 0.38 ? 0.58 : rel < 0.62 ? 0.82 : 1;
        const byAbs = it.prom < 44 ? 0.6 : it.prom < 80 ? 0.85 : 1;
        alpha = Math.min(byRel, byAbs);
      }

      const label = it.ob.label || '';
      const tag = o.tags ? it.ob.tag : '';
      const font = it.sel ? LABEL_FONT_SEL : alpha >= 0.82 ? LABEL_FONT_NEAR : LABEL_FONT;
      ctx.font = font;
      const tw = label ? ctx.measureText(label).width : 0;
      ctx.font = TAG_FONT;
      const gwid = tag ? ctx.measureText(tag).width + 14 : 0;
      const w = Math.max(label ? tw + LABEL_PAD * 2 : 0, gwid);
      const h = (label ? LABEL_H : 0) + (tag ? TAG_H : 0);
      if (!w || !h) continue;

      const lead = it.ob.kind === 'conn' ? 12 : 24;
      const baseY = it.ay - lead - h;

      // Score every legal seat and take the best, rather than the first that
      // fits. This is what makes a label choose clear sky over a vessel's
      // flank: the seat order still pulls it toward its own machine, but a
      // plate's worth of hidden equipment outweighs several steps of distance.
      let best: { x: number; y: number; cover: number; lateral: number } | null = null;
      let bestScore = Infinity;
      for (let s = 0; s < LABEL_SEATS.length; s++) {
        const seat = LABEL_SEATS[s];
        // Step far enough sideways that the horizontal arm of the dog-leg
        // reads as an arm and not as a kink in a vertical line.
        const lateral = seat[0] === 0 ? 0 : Math.sign(seat[0]) * (w / 2 + 18 + (Math.abs(seat[0]) - 1) * 44);
        const x = it.ax + lateral;
        // Clamp the seat into the viewport rather than reject it: a vessel tall
        // enough for its top to leave the frame is still the biggest thing on
        // screen and still needs its name.
        const y = Math.min(Math.max(2, baseY + seat[1] * (h + 6)), this.H - h - 2);
        if (x - w / 2 < 2 || x + w / 2 > this.W - 2) continue;
        // Reserve three pixels of air around the plate: chips that merely
        // touch read as one run-together block of text.
        const r = { x: x - w / 2 - 3, y: y - 3, w: w + 6, h: h + 6 };
        if (placed.some((p) => r.x < p.x + p.w && r.x + r.w > p.x && r.y < p.y + p.h && r.y + r.h > p.y)) continue;
        const cover = coverage(x - w / 2, y, x + w / 2, y + h);
        const score = cover + s * LABEL_SEAT_COST;
        if (score < bestScore) {
          bestScore = score;
          best = { x, y, cover, lateral };
          // Clear sky at the nearest seat: nothing can beat that, so stop.
          if (cover < 0.02 && s === 0) break;
        }
      }
      if (!best) continue;
      placed.push({ x: best.x - w / 2 - 3, y: best.y - 3, w: w + 6, h: h + 6 });
      chips.push({
        it,
        x: best.x,
        y: best.y,
        w,
        h,
        label,
        tag,
        font,
        alpha,
        lateral: best.lateral,
        cover: best.cover,
      });
    }

    // Leaders first, plates second — so every arm passes UNDER every chip, and
    // a leader that has to cross the field never draws itself through somebody
    // else's text.
    ctx.lineJoin = 'round';
    ctx.lineCap = 'butt';
    for (const c of chips) {
      const it = c.it;
      ctx.globalAlpha = c.alpha;
      ctx.strokeStyle = it.sel ? 'rgba(0,120,212,0.9)' : 'rgba(40,48,60,0.45)';
      ctx.lineWidth = it.sel ? 1.6 : 1;
      ctx.beginPath();
      ctx.moveTo(it.ax, it.ay);
      if (c.lateral === 0) {
        ctx.lineTo(it.ax, c.y + c.h);
      } else {
        // Up the object's own column to the chip's height, then across to its
        // near edge: an L, the way a drawing office runs a callout.
        const midY = c.y + c.h / 2;
        ctx.lineTo(it.ax, midY);
        ctx.lineTo(c.x - Math.sign(c.lateral) * (c.w / 2), midY);
      }
      ctx.stroke();

      // Anchor dot. Without it a dog-legged label is ambiguous about which of
      // two neighbouring machines it belongs to.
      ctx.beginPath();
      ctx.arc(it.ax, it.ay, it.sel ? 3 : 2.2, 0, Math.PI * 2);
      ctx.fillStyle = it.sel ? '#0078d4' : 'rgba(40,48,60,0.6)';
      ctx.fill();
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const c of chips) {
      const it = c.it;
      ctx.globalAlpha = c.alpha;
      let y = c.y;
      if (c.tag) {
        ctx.fillStyle = it.sel ? '#0078d4' : '#2b579a';
        this.roundRect(ctx, c.x - c.w / 2, y, c.w, TAG_H - 1, 3);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = TAG_FONT;
        ctx.fillText(c.tag, c.x, y + (TAG_H - 1) / 2);
        y += TAG_H;
      }
      if (c.label) {
        // The plate's weight follows what is behind it. Over open sky there is
        // nothing to cover and nothing to fight, so it fades to a whisper that
        // merely settles the text; over dark machinery it goes solid and does
        // the job a halo could not. Either way the reader sees as much of the
        // plant as the label can afford to leave them.
        const solid = Math.min(1, c.cover * 3.2);
        this.roundRect(ctx, c.x - c.w / 2, y, c.w, LABEL_H, 4);
        ctx.fillStyle = it.sel
          ? 'rgba(255,255,255,0.96)'
          : `rgba(252,253,255,${(0.5 + 0.4 * solid).toFixed(3)})`;
        ctx.fill();
        ctx.strokeStyle = it.sel
          ? 'rgba(0,120,212,0.95)'
          : `rgba(38,48,64,${(0.07 + 0.15 * solid).toFixed(3)})`;
        ctx.lineWidth = it.sel ? 1.4 : 1;
        ctx.stroke();
        // A hairline of the plate colour under the glyphs, so text over a very
        // light plate still separates from whatever shows through it.
        ctx.font = c.font;
        if (solid < 0.5) {
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;
          ctx.strokeStyle = `rgba(252,253,255,${(0.75 - 0.5 * solid).toFixed(3)})`;
          ctx.lineWidth = 2.4;
          ctx.strokeText(c.label, c.x, y + LABEL_H / 2 + 0.5);
        }
        ctx.fillStyle = it.sel ? '#0a63b0' : '#182029';
        ctx.fillText(c.label, c.x, y + LABEL_H / 2 + 0.5);
      }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * Live lengths on the run being drawn.
   *
   * A pipe is committed one click at a time, and until now the only way to find
   * out how long a leg was going to be was to place it, dimension it, read the
   * figure and undo. So the leg under the cursor carries its length as it is
   * dragged, the legs already down carry theirs faintly, and the total sits at
   * the far end — enough to draw to a size rather than to draw and then check.
   *
   * Nothing here is a dimension. It exists only while the draft does, and
   * leaves no object behind: the reader who wants a permanent figure asks for
   * one with the dimension tool.
   */
  private drawDraftLengths(
    ctx: CanvasRenderingContext2D,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    const pts = o.draftPts;
    if (!pts || pts.length < 2) return;
    const hs = o.heightScale;
    const on = pts.map((p) => this.proj(v3(p.x, p.y * hs, p.z), view, projK, clipZ, cx, cy));

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';

    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      total += Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
      const A = on[i - 1];
      const B = on[i];
      if (!A || !B) continue;
      // The leg under the cursor is the one being decided, so it is the one
      // that gets read. The rest are context and stay quiet.
      const live = i === pts.length - 1;
      const px = Math.hypot(B.x - A.x, B.y - A.y);
      if (px < (live ? 16 : 34)) continue;

      const txt = formatLength(Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z) * o.scaleMm);
      ctx.font = live ? 'bold 12px Segoe UI, system-ui' : '600 10.5px Segoe UI, system-ui';
      const tw = ctx.measureText(txt).width;

      // Offset clear of the leg, on the side away from the run so the figure
      // never sits on the pipe it is measuring.
      const ux = (B.x - A.x) / px;
      const uy = (B.y - A.y) / px;
      const off = live ? 22 : 17;
      const tx = (A.x + B.x) / 2 - uy * off;
      const ty = (A.y + B.y) / 2 + ux * off;

      if (live) {
        this.roundRect(ctx, tx - tw / 2 - 6, ty - 9.5, tw + 12, 19, 4);
        ctx.fillStyle = 'rgba(0,120,212,0.94)';
        ctx.fill();
        // A tick back to the leg, so a long offset still reads as belonging.
        ctx.strokeStyle = 'rgba(0,120,212,0.55)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo((A.x + B.x) / 2, (A.y + B.y) / 2);
        ctx.lineTo(tx + uy * 9, ty - ux * 9);
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.fillText(txt, tx, ty + 0.5);
      } else {
        // No plate on the committed legs: several of them plated would compete
        // with the one number the reader is actually watching.
        ctx.strokeStyle = 'rgba(255,255,255,0.85)';
        ctx.lineWidth = 2.6;
        ctx.miterLimit = 2;
        ctx.strokeText(txt, tx, ty + 0.5);
        ctx.fillStyle = '#2a3542';
        ctx.fillText(txt, tx, ty + 0.5);
      }
    }

    // The running total, at the head of the run. Only once there is more than
    // one leg — on a single leg it would just repeat the live figure.
    const head = on[on.length - 1];
    if (head && pts.length > 2) {
      const txt = 'total ' + formatLength(total * o.scaleMm);
      ctx.font = '600 11px Segoe UI, system-ui';
      const tw = ctx.measureText(txt).width;
      const ty = head.y + 30;
      this.roundRect(ctx, head.x - tw / 2 - 7, ty - 9, tw + 14, 18, 9);
      ctx.fillStyle = 'rgba(24,32,41,0.9)';
      ctx.fill();
      ctx.fillStyle = '#eaf2fb';
      ctx.fillText(txt, head.x, ty + 0.5);
    }
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }


  /**
   * Floor datum down the near-left edge of the plant.
   *
   * A storey marker, exactly as a building section would carry: one vertical
   * line, a tick at every floor, and the floor's name and height beside it. It
   * is the only thing in the view that answers "which floor am I looking at?"
   * without the reader having to count decks.
   *
   * Nothing is drawn for a single-storey plant. Floors are written by hand, so
   * until somebody has written one there is no storey to mark — and a mast
   * labelled "Ground Floor +0" beside a flat drawing states the obvious while
   * looking like a setting that got left on.
   */
  private drawFloorDatum(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    if (scene.levels.length < 2 && !(scene.levels[0]?.elevation > 0)) return;
    const x = scene.min.x - 70;
    const z = scene.max.z + 70;
    const hs = o.heightScale;
    const top = scene.levels[scene.levels.length - 1].elevation * hs;

    // The mast, from the ground to the highest floor.
    const a = this.proj(v3(x, 0, z), view, projK, clipZ, cx, cy);
    const b = this.proj(v3(x, Math.max(top, 20), z), view, projK, clipZ, cx, cy);
    if (a && b) {
      ctx.save();
      ctx.strokeStyle = 'rgba(43,87,154,0.55)';
      ctx.lineWidth = 1.4;
      ctx.setLineDash([7, 5]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();
    }

    // Far floors first, so a near one overlaps rather than being overlapped.
    const marks = scene.levels
      .map((l) => ({ l, p: this.proj(v3(x, l.elevation * hs, z), view, projK, clipZ, cx, cy) }))
      .filter((m) => !!m.p)
      .sort((m, n) => (m.p!.z ?? 0) - (n.p!.z ?? 0));

    ctx.textBaseline = 'middle';
    for (const m of marks) {
      const p = m.p!;
      if (p.x < -260 || p.x > this.W + 260 || p.y < -120 || p.y > this.H + 120) continue;
      // Tick running back toward the plant, so the marker reads as attached
      // to the floor rather than floating beside it.
      const t = this.proj(v3(x + 52, m.l.elevation * hs, z - 52), view, projK, clipZ, cx, cy);
      if (t) {
        ctx.strokeStyle = 'rgba(43,87,154,0.7)';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(t.x, t.y);
        ctx.stroke();
      }

      ctx.font = 'bold 11.5px Segoe UI, system-ui';
      const name = m.l.name;
      const elev = `+${Math.round(m.l.elevation)}`;
      const nw = ctx.measureText(name).width;
      ctx.font = '10px Segoe UI, system-ui';
      const ew = ctx.measureText(elev).width;
      const w = Math.max(nw, ew) + 16;
      const h = 30;
      const bx = p.x - w - 10;
      const by = p.y - h / 2;

      ctx.fillStyle = 'rgba(43,87,154,0.94)';
      this.roundRect(ctx, bx, by, w, h, 4);
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11.5px Segoe UI, system-ui';
      ctx.fillText(name, bx + w / 2, by + 10);
      ctx.fillStyle = 'rgba(255,255,255,0.75)';
      ctx.font = '10px Segoe UI, system-ui';
      ctx.fillText(elev, bx + w / 2, by + 21);

      // A dot on the mast itself, so the pill is unmistakably tied to it.
      ctx.fillStyle = '#2b579a';
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * Dimension every straight leg of pipework.
   *
   * Drawn the way a draughtsman would: the dimension line sits OFF TO THE SIDE
   * of the pipe on its own offset, joined back to the leg by two light
   * extension lines, with the figure set INTO a break in that line. Nothing is
   * drawn across the pipe itself — a number stamped on the middle of a run
   * hides the very thing it is measuring.
   *
   * Which side it goes is decided per leg: whichever way points away from the
   * middle of the drawing, so dimensions fan outward instead of piling into the
   * equipment.
   */
  private drawDimensions(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    if (!scene.dims.length) return;
    const hs = o.heightScale;
    const only = o.dimsSelectedOnly && o.selected.size > 0;
    /** A selected leg counts as its run being selected. */
    const runSelected = (id: string) => {
      if (o.selected.has(id)) return true;
      for (const sid of o.selected) if (sid.startsWith(LEG_PREFIX) && legRun(sid) === id) return true;
      return false;
    };

    // Screen position of the drawing's middle, used to pick the offset side.
    const mid = this.proj(
      v3(
        (scene.min.x + scene.max.x) / 2,
        ((scene.min.y + scene.max.y) / 2) * hs,
        (scene.min.z + scene.max.z) / 2,
      ),
      view,
      projK,
      clipZ,
      cx,
      cy,
    );
    const hubX = mid ? mid.x : cx;
    const hubY = mid ? mid.y : cy;

    const OFF = 26; // offset of the dimension line from the pipe, px
    const GAP = 7; // clear space between the pipe and its extension line, px
    const MIN_PX = 46; // shorter than this on screen and the figure won't fit

    const placed: { x: number; y: number; w: number; h: number }[] = [];

    // Nearest first: a dimension in front should win the space.
    const legs = scene.dims
      .filter((d) => !only || runSelected(d.id))
      .map((d) => {
        const A = this.proj(v3(d.a.x, d.a.y * hs, d.a.z), view, projK, clipZ, cx, cy);
        const B = this.proj(v3(d.b.x, d.b.y * hs, d.b.z), view, projK, clipZ, cx, cy);
        const len = Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y, d.b.z - d.a.z);
        return { d, A, B, len, z: A && B ? (A.z + B.z) / 2 : -Infinity };
      })
      .filter((l) => l.A && l.B)
      .sort((a, b) => b.z - a.z);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const leg of legs) {
      const A = leg.A!;
      const B = leg.B!;
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const px = Math.hypot(dx, dy);
      if (px < MIN_PX) continue;
      const ux = dx / px;
      const uy = dy / px;
      // Perpendicular, flipped to point away from the middle of the drawing.
      let nx = -uy;
      let ny = ux;
      const mx = (A.x + B.x) / 2;
      const my = (A.y + B.y) / 2;
      if (nx * (mx - hubX) + ny * (my - hubY) < 0) {
        nx = -nx;
        ny = -ny;
      }

      const a1 = { x: A.x + nx * OFF, y: A.y + ny * OFF };
      const b1 = { x: B.x + nx * OFF, y: B.y + ny * OFF };
      const tx = (a1.x + b1.x) / 2;
      const ty = (a1.y + b1.y) / 2;

      const id = legId(leg.d.id, leg.d.seg);
      const sel = o.selected.has(id);
      // A caption replaces the measurement; that is the whole point of typing
      // one. The measured value is still what the length input reads back.
      const txt = leg.d.text && leg.d.text !== '' ? leg.d.text : formatLength(leg.len * o.scaleMm);
      ctx.font = sel ? 'bold 11.5px Segoe UI, system-ui' : '600 11px Segoe UI, system-ui';
      const tw = ctx.measureText(txt).width;
      const box = { x: tx - tw / 2 - 5, y: ty - 9, w: tw + 10, h: 18 };
      if (
        placed.some(
          (q) => box.x < q.x + q.w && box.x + box.w > q.x && box.y < q.y + q.h && box.y + box.h > q.y,
        )
      )
        continue;
      placed.push(box);

      // Extension lines, starting clear of the pipe and running a little past
      // the dimension line, exactly as they would on a drawing.
      ctx.strokeStyle = sel ? 'rgba(0,120,212,0.65)' : 'rgba(28,40,58,0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(A.x + nx * GAP, A.y + ny * GAP);
      ctx.lineTo(A.x + nx * (OFF + 5), A.y + ny * (OFF + 5));
      ctx.moveTo(B.x + nx * GAP, B.y + ny * GAP);
      ctx.lineTo(B.x + nx * (OFF + 5), B.y + ny * (OFF + 5));
      ctx.stroke();

      // The dimension line, broken either side of the figure so the number sits
      // IN the line rather than on top of it.
      const half = tw / 2 + 6;
      ctx.strokeStyle = sel ? '#0078d4' : 'rgba(28,40,58,0.8)';
      ctx.lineWidth = sel ? 1.6 : 1.2;
      ctx.beginPath();
      ctx.moveTo(a1.x, a1.y);
      ctx.lineTo(tx - ux * half, ty - uy * half);
      ctx.moveTo(tx + ux * half, ty + uy * half);
      ctx.lineTo(b1.x, b1.y);
      ctx.stroke();

      // Arrowheads pointing outward into the extension lines.
      const arrow = (at: { x: number; y: number }, sx: number, sy: number) => {
        ctx.beginPath();
        ctx.moveTo(at.x, at.y);
        ctx.lineTo(at.x + sx * 8 - sy * 2.6, at.y + sy * 8 + sx * 2.6);
        ctx.lineTo(at.x + sx * 8 + sy * 2.6, at.y + sy * 8 - sx * 2.6);
        ctx.closePath();
        ctx.fill();
      };
      ctx.fillStyle = sel ? '#0078d4' : 'rgba(28,40,58,0.8)';
      arrow(a1, ux, uy);
      arrow(b1, -ux, -uy);

      // A plate behind the selected figure, so it reads as the thing you are
      // about to type into rather than as one more number on the drawing.
      if (sel) {
        this.roundRect(ctx, box.x, box.y, box.w, box.h, 4);
        ctx.fillStyle = '#0078d4';
        ctx.fill();
      }
      ctx.fillStyle = sel ? '#fff' : leg.d.text ? '#8a3a10' : '#1b2634';
      ctx.fillText(txt, tx, ty + 0.5);

      // Pickable, exactly like a hand-placed dimension: this is what makes a
      // leg figure something you can click and edit rather than a read-out.
      this.dimHits.push({ id, x: box.x - 4, y: box.y - 4, w: box.w + 8, h: box.h + 8 });
    }
  }

  /**
   * Draw the dimensions placed by hand.
   *
   * Same draughtsman's construction as the automatic ones — an offset
   * dimension line, extension lines back to the two points, arrowheads, and the
   * figure set INTO a break in the line so it never covers what it measures.
   * The difference is that these carry whatever text was typed on them, and
   * they are always drawn: they were put there deliberately.
   */
  private drawUserDimensions(
    ctx: CanvasRenderingContext2D,
    scene: Scene,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    o: RenderOptions,
  ): void {
    if (!scene.userDims.length) return;
    const hs = o.heightScale;

    for (const d of scene.userDims) {
      const A = this.proj(v3(d.a.x, d.a.y * hs, d.a.z), view, projK, clipZ, cx, cy);
      const B = this.proj(v3(d.b.x, d.b.y * hs, d.b.z), view, projK, clipZ, cx, cy);
      if (!A || !B) continue;
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const px = Math.hypot(dx, dy);
      // A dimension seen exactly end-on has no direction to offset along.
      const ux = px > 1 ? dx / px : 1;
      const uy = px > 1 ? dy / px : 0;
      const sgn = d.flip ? -1 : 1;
      const nx = -uy * sgn;
      const ny = ux * sgn;
      const OFF = d.off;
      const GAP = 7;

      const sel = o.selected.has(d.id);
      const a1 = { x: A.x + nx * OFF, y: A.y + ny * OFF };
      const b1 = { x: B.x + nx * OFF, y: B.y + ny * OFF };
      const tx = (a1.x + b1.x) / 2;
      const ty = (a1.y + b1.y) / 2;

      ctx.font = sel ? '700 12px Segoe UI, system-ui' : '600 11.5px Segoe UI, system-ui';
      const tw = ctx.measureText(d.text).width;

      const stroke = sel ? 'rgba(0,120,212,0.95)' : 'rgba(180,70,20,0.9)';
      ctx.strokeStyle = sel ? 'rgba(0,120,212,0.6)' : 'rgba(180,70,20,0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(A.x + nx * GAP, A.y + ny * GAP);
      ctx.lineTo(A.x + nx * (OFF + 5), A.y + ny * (OFF + 5));
      ctx.moveTo(B.x + nx * GAP, B.y + ny * GAP);
      ctx.lineTo(B.x + nx * (OFF + 5), B.y + ny * (OFF + 5));
      ctx.stroke();

      const half = tw / 2 + 7;
      ctx.strokeStyle = stroke;
      ctx.lineWidth = sel ? 1.8 : 1.3;
      ctx.beginPath();
      if (px > half * 2 + 8) {
        ctx.moveTo(a1.x, a1.y);
        ctx.lineTo(tx - ux * half, ty - uy * half);
        ctx.moveTo(tx + ux * half, ty + uy * half);
        ctx.lineTo(b1.x, b1.y);
      } else {
        // Too short to break: draw it whole and let the figure sit above it.
        ctx.moveTo(a1.x, a1.y);
        ctx.lineTo(b1.x, b1.y);
      }
      ctx.stroke();

      const arrow = (at: { x: number; y: number }, sx: number, sy: number) => {
        ctx.beginPath();
        ctx.moveTo(at.x, at.y);
        ctx.lineTo(at.x + sx * 9 - sy * 3, at.y + sy * 9 + sx * 3);
        ctx.lineTo(at.x + sx * 9 + sy * 3, at.y + sy * 9 - sx * 3);
        ctx.closePath();
        ctx.fill();
      };
      ctx.fillStyle = stroke;
      arrow(a1, ux, uy);
      arrow(b1, -ux, -uy);

      // Small ticks marking exactly which two points are being measured.
      ctx.fillStyle = stroke;
      for (const q of [A, B]) {
        ctx.beginPath();
        ctx.arc(q.x, q.y, sel ? 3.4 : 2.6, 0, Math.PI * 2);
        ctx.fill();
      }

      const boxH = 19;
      const short = px <= half * 2 + 8;
      const bx = tx - tw / 2 - 6;
      const by = ty - boxH / 2 - (short ? boxH * 0.8 : 0);
      if (short || sel) {
        ctx.fillStyle = sel ? 'rgba(0,120,212,0.95)' : 'rgba(255,255,255,0.92)';
        this.roundRect(ctx, bx, by, tw + 12, boxH, 4);
        ctx.fill();
        if (!sel) {
          ctx.strokeStyle = 'rgba(180,70,20,0.5)';
          ctx.lineWidth = 1;
          this.roundRect(ctx, bx, by, tw + 12, boxH, 4);
          ctx.stroke();
        }
      }
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = sel ? '#fff' : '#8a3a10';
      ctx.fillText(d.text, tx, by + boxH / 2 + 0.5);

      this.dimHits.push({
        id: d.id,
        x: bx - 4,
        y: by - 4,
        w: tw + 20,
        h: boxH + 8,
        seg: [a1.x, a1.y, b1.x, b1.y],
      });
    }
  }

  /** The dimension under the cursor — its figure, or its line. */
  pickDim(px: number, py: number): string | null {
    // Last drawn wins: it is the one on top.
    for (let i = this.dimHits.length - 1; i >= 0; i--) {
      const r = this.dimHits[i];
      if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) return r.id;
    }
    // Then the lines. Figures win over lines everywhere, so a figure sitting on
    // another dimension's line is still the thing you get when you click it.
    let best: string | null = null;
    let bestD = DIM_LINE_REACH;
    for (let i = this.dimHits.length - 1; i >= 0; i--) {
      const r = this.dimHits[i];
      if (!r.seg) continue;
      const d = distToSegment(px, py, r.seg[0], r.seg[1], r.seg[2], r.seg[3]);
      if (d < bestD) {
        bestD = d;
        best = r.id;
      }
    }
    return best;
  }

  // -- Handle overlay --------------------------------------------------

  private ensureOverlay(n: number): void {
    if (n <= this.ovCap) return;
    this.ovCap = Math.max(512, n * 2);
    this.ovSx = new Float32Array(this.ovCap * 8);
    this.ovNp = new Uint8Array(this.ovCap);
    this.ovCol = new Int32Array(this.ovCap);
    this.ovDep = new Float32Array(this.ovCap);
    this.ovObj = new Int32Array(this.ovCap);
  }

  /**
   * Draw the drag handles on top of the finished frame.
   *
   * A few hundred triangles at most, so this takes the straightforward path
   * rather than the main pass's bucketed one — and it deliberately ignores the
   * scene's depth entirely. A handle you cannot see is a handle you cannot use.
   * Triangles crossing the near plane are simply dropped: gizmos sit near the
   * camera target, so it never comes up in practice and clipping them would add
   * a branch to no purpose.
   */
  private paintOverlay(
    ctx: CanvasRenderingContext2D,
    ov: Overlay,
    view: Float64Array,
    projK: (z: number) => number,
    clipZ: number,
    cx: number,
    cy: number,
    eye: Vec3,
  ): void {
    this.ensureOverlay(ov.count);
    let vis = 0;
    for (let t = 0; t < ov.count; t++) {
      const b = t * 9;
      let behind = 0;
      const vx = [0, 0, 0];
      const vy = [0, 0, 0];
      const vz = [0, 0, 0];
      for (let k = 0; k < 3; k++) {
        const i = b + k * 3;
        const px = ov.pos[i];
        const py = ov.pos[i + 1];
        const pz = ov.pos[i + 2];
        vx[k] = view[0] * px + view[4] * py + view[8] * pz + view[12];
        vy[k] = view[1] * px + view[5] * py + view[9] * pz + view[13];
        vz[k] = view[2] * px + view[6] * py + view[10] * pz + view[14];
        if (vz[k] > clipZ) behind++;
      }
      if (behind) continue;

      let nx = ov.nrm[b] + ov.nrm[b + 3] + ov.nrm[b + 6];
      let ny = ov.nrm[b + 1] + ov.nrm[b + 4] + ov.nrm[b + 7];
      let nz = ov.nrm[b + 2] + ov.nrm[b + 5] + ov.nrm[b + 8];
      const nl = Math.hypot(nx, ny, nz);
      if (nl < 1e-6) continue;
      nx /= nl;
      ny /= nl;
      nz /= nl;

      const c0x = (ov.pos[b] + ov.pos[b + 3] + ov.pos[b + 6]) / 3;
      const c0y = (ov.pos[b + 1] + ov.pos[b + 4] + ov.pos[b + 7]) / 3;
      const c0z = (ov.pos[b + 2] + ov.pos[b + 5] + ov.pos[b + 8]) / 3;
      let ex = eye.x - c0x;
      let ey = eye.y - c0y;
      let ez = eye.z - c0z;
      const el = Math.hypot(ex, ey, ez) || 1;
      ex /= el;
      ey /= el;
      ez /= el;
      if (nx * ex + ny * ey + nz * ez < 0) continue;

      const off = vis * 8;
      for (let k = 0; k < 3; k++) {
        const kk = projK(vz[k]);
        this.ovSx[off + k * 2] = cx + vx[k] * kk;
        this.ovSx[off + k * 2 + 1] = cy - vy[k] * kk;
      }
      this.ovNp[vis] = 3;
      this.ovCol[vis] = shade(v3(nx, ny, nz), v3(ex, ey, ez), MATS[ov.mat[t]], null, 0);
      this.ovDep[vis] = -(vz[0] + vz[1] + vz[2]) / 3;
      this.ovObj[vis] = ov.obj[t];
      vis++;
    }
    this.ovN = vis;

    this.ovOrder.length = vis;
    for (let i = 0; i < vis; i++) this.ovOrder[i] = i;
    this.ovOrder.sort((a, b) => this.ovDep[b] - this.ovDep[a]);

    ctx.globalAlpha = 1;
    ctx.lineJoin = 'round';
    ctx.lineWidth = 0.8;
    let cur = -1;
    let open = false;
    const flush = () => {
      if (!open) return;
      const s = cssColor(cur);
      ctx.fillStyle = s;
      ctx.fill();
      ctx.strokeStyle = s;
      ctx.stroke();
      open = false;
    };
    for (let i = 0; i < vis; i++) {
      const t = this.ovOrder[i];
      if (this.ovCol[t] !== cur) {
        flush();
        cur = this.ovCol[t];
        ctx.beginPath();
        open = true;
      }
      const off = t * 8;
      ctx.moveTo(this.ovSx[off], this.ovSx[off + 1]);
      ctx.lineTo(this.ovSx[off + 2], this.ovSx[off + 3]);
      ctx.lineTo(this.ovSx[off + 4], this.ovSx[off + 5]);
      ctx.closePath();
    }
    flush();
  }

  /** Index of the handle under the cursor, or -1. Uses last frame's overlay. */
  pickOverlay(px: number, py: number): number {
    let best = -1;
    let bestDepth = Infinity;
    for (let i = 0; i < this.ovN; i++) {
      // A few pixels of slack: handles are small and a near-miss should still
      // grab rather than start an orbit.
      if (!this.pointInPolyOv(px, py, i * 8, this.ovNp[i])) continue;
      if (this.ovDep[i] < bestDepth) {
        bestDepth = this.ovDep[i];
        best = this.ovObj[i];
      }
    }
    return best;
  }

  private pointInPolyOv(px: number, py: number, off: number, n: number): boolean {
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = this.ovSx[off + i * 2];
      const yi = this.ovSx[off + i * 2 + 1];
      const xj = this.ovSx[off + j * 2];
      const yj = this.ovSx[off + j * 2 + 1];
      if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }

  // ── Picking ────────────────────────────────────────────────────────────

  /**
   * View-space depth of the frontmost triangle under the cursor, or null.
   * Zoom-to-cursor needs the DISTANCE to what you are pointing at, not just
   * which object it is.
   */
  pickDepth(px: number, py: number): number | null {
    let best = Infinity;
    for (let i = 0; i < this.visible; i++) {
      if (!this.pointInPoly(px, py, i * 8, this.np[i])) continue;
      if (this.dep[i] < best) best = this.dep[i];
    }
    return Number.isFinite(best) ? best : null;
  }

  /**
   * Every object under the cursor, nearest first.
   * On a dense drawing the thing you want is often behind two pipes and a
   * handrail; this is what lets repeated clicks step through to it.
   */
  pickAll(px: number, py: number, scene: Scene): SceneObject[] {
    const nearest = new Map<number, number>();
    for (let i = 0; i < this.visible; i++) {
      if (!this.pointInPoly(px, py, i * 8, this.np[i])) continue;
      const ob = this.objOf[i];
      const prev = nearest.get(ob);
      if (prev === undefined || this.dep[i] < prev) nearest.set(ob, this.dep[i]);
    }
    return [...nearest.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([ob]) => scene.objects[ob])
      .filter((o): o is SceneObject => !!o);
  }

  /**
   * Which object is under the cursor, using LAST FRAME's projected triangles.
   * No separate ray cast and no ID buffer: the frame already computed exactly
   * the geometry the user can see, so hit-testing it is both cheaper and
   * automatically consistent with what is on screen.
   */
  pick(px: number, py: number, scene: Scene): SceneObject | null {
    let best = -1;
    let bestDepth = Infinity;
    for (let i = 0; i < this.visible; i++) {
      const off = i * 8;
      const pts = this.np[i];
      if (!this.pointInPoly(px, py, off, pts)) continue;
      if (this.dep[i] < bestDepth) {
        bestDepth = this.dep[i];
        best = this.objOf[i];
      }
    }
    return best >= 0 ? (scene.objects[best] ?? null) : null;
  }

  private pointInPoly(px: number, py: number, off: number, n: number): boolean {
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = this.sx[off + i * 2];
      const yi = this.sx[off + i * 2 + 1];
      const xj = this.sx[off + j * 2];
      const yj = this.sx[off + j * 2 + 1];
      if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }
}

/** Convenience for callers that want the light direction (e.g. a compass). */
export const lightDirection = (): Vec3 => vNorm(KEY_DIR);
